import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Palette, ArrowLeft, Loader2, RefreshCw, IndianRupee, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS = {
  submitted: { label: "Awaiting price", cls: "bg-amber-100 text-amber-700" },
  quoted: { label: "Quoted", cls: "bg-blue-100 text-blue-700" },
  paid: { label: "Paid", cls: "bg-green-100 text-green-700" },
};

export default function AdminDesigns() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({});
  const [notes, setNotes] = useState({});
  const [savingId, setSavingId] = useState(null);

  useEffect(() => { if (user === false) navigate("/admin", { replace: true }); }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/designs"); setDesigns(data); }
    catch { toast.error("Failed to load designs"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user && user.email) load(); }, [user, load]);

  const saveQuote = async (d) => {
    const amount = prices[d.id];
    if (!amount || Number(amount) < 1) { toast.error("Enter a valid price"); return; }
    setSavingId(d.id);
    try {
      await api.post(`/admin/designs/${d.id}/quote`, { amount: Number(amount), note: notes[d.id] || "" });
      toast.success(`Quote sent — customer can now pay ₹${amount}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save quote"); }
    finally { setSavingId(null); }
  };

  const previewUrl = (d) => {
    if (!d.preview_path) return null;
    const token = localStorage.getItem("cp_token");
    return `${API_BASE}/files/${d.preview_path}?auth=${token}`;
  };

  if (!user || !user.email) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 flex h-16 items-center justify-between">
          <div className="flex items-center gap-2.5"><span className="grid h-9 w-9 place-items-center rounded-md bg-primary text-white"><Palette size={20} /></span><h1 className="font-heading text-base font-extrabold text-slate-900">Design Quotes</h1></div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={load} data-testid="designs-refresh"><RefreshCw size={15} /> Refresh</Button>
            <Link to="/admin/dashboard"><Button variant="outline" size="sm" className="gap-2"><ArrowLeft size={15} /> Dashboard</Button></Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <p className="mb-5 text-sm text-slate-500">Customer designs from the Design Studio. Set a print price — the customer can then pay online.</p>
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-primary" /></div>
        ) : designs.length === 0 ? (
          <div data-testid="no-designs" className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center text-slate-400"><Palette size={40} className="mx-auto" /><p className="mt-3">No design submissions yet</p></div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {designs.map((d) => {
              const st = STATUS[d.quote_status] || STATUS.submitted;
              const url = previewUrl(d);
              return (
                <div key={d.id} data-testid={`design-${d.id}`} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  {url && <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={d.product} className="w-full h-44 object-contain bg-slate-100 border-b border-slate-200" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} /></a>}
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-slate-900">{d.name || "Customer"}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${st.cls}`}>{st.label}</span>
                    </div>
                    <p className="text-sm text-slate-500">{d.phone}</p>
                    <p className="mt-1 text-sm text-slate-700">{d.product} · {d.print_size}</p>
                    {d.notes && <p className="mt-1 text-xs text-slate-500 italic">"{d.notes}"</p>}

                    {d.quote_status === "paid" ? (
                      <div className="mt-3 flex items-center gap-2 text-green-700 font-semibold"><CheckCircle2 size={18} /> Paid ₹{d.quote_amount}</div>
                    ) : (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <IndianRupee size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input data-testid={`price-${d.id}`} type="number" placeholder="Price" className="pl-7 h-9" defaultValue={d.quote_amount || ""} onChange={(e) => setPrices((p) => ({ ...p, [d.id]: e.target.value }))} />
                          </div>
                          <Button data-testid={`quote-save-${d.id}`} size="sm" className="bg-primary hover:bg-amber-700 text-white h-9" onClick={() => saveQuote(d)} disabled={savingId === d.id}>
                            {savingId === d.id ? <Loader2 size={15} className="animate-spin" /> : (d.quote_status === "quoted" ? "Update" : "Send quote")}
                          </Button>
                        </div>
                        <Input data-testid={`note-${d.id}`} placeholder="Optional note to customer" className="h-9 text-sm" defaultValue={d.quote_note || ""} onChange={(e) => setNotes((n) => ({ ...n, [d.id]: e.target.value }))} />
                        {d.quote_status === "quoted" && <p className="text-xs text-blue-600">Quoted ₹{d.quote_amount} — waiting for payment.</p>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
